import { selectUserInfo, UserInfoState } from 'store/slices/userInfoSlice';
import { Trans } from 'react-i18next';
import { groupBy, sortBy, cloneDeep } from 'lodash-es';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import LOCALS from './locals';
import {
  Home,
  Package,
  ShoppingCart,
  Users,
  ShoppingBag,
  TrendingUp,
  FileText,
  Calculator,
  Settings,
  BarChart3,
} from 'lucide-react';

export type MenuConfigItem = {
  path: string;
  key?: string;
  icon?: JSX.Element;
  label: JSX.Element | string;
  children?: MenuConfigItem[];
  sort?: number;
};

// 菜单配置
export const ALL_MENU_CONFIG_ITEMS: MenuConfigItem[] = [
  // 主面板
  {
    path: 'home',
    icon: <Home className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.home_page} />,
    sort: 0,
  },
  // 商品
  {
    path: 'pms',
    icon: <Package className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.product} />,
    sort: 2,
    children: [
      {
        path: 'product-list', // 商品列表
        label: <Trans i18nKey={LOCALS.product_list} />,
      },
      {
        path: 'product-list-mobile', // 商品列表
        label: <Trans i18nKey={LOCALS.product_list_mobile} />,
      },
      {
        path: 'product-activity',
        label: <Trans i18nKey={LOCALS.product_operation_record} />,
      },
      {
        path: 'store-transfer',
        label: '跨店调货',
      },
      {
        path: 'album-batch-upload',
        label: <Trans i18nKey={LOCALS.album_batch_upload} />,
      },
      {
        path: 'stock-taking-list',
        label: <Trans i18nKey={LOCALS.eNxVOVImMN} />,
      },
      {
        path: 'product-want-request',
        label: <Trans i18nKey={LOCALS.product_want_request} />,
      },
      // {
      //   path: 'product-add',
      //   icon: <FileAddOutlined />,
      //   label: <Trans i18nKey={LOCALS.product_add} />,
      // },
      // {
      //   path: 'addSalesLink',
      //   icon: <FileAddOutlined />,
      //   label: <Trans i18nKey={LOCALS.add_sales_link} />,
      // },
      // {
      //   path: 'label',
      //   icon: <UnorderedListOutlined />,
      //   label: <Trans i18nKey={LOCALS.LabelList} />,
      // },
      {
        path: 'product-box',
        label: 'Box尺寸',
      },
      {
        path: 'product-cate',
        label: <Trans i18nKey={LOCALS.product_categories} />,
      },
      {
        path: 'product-material',
        label: '商品材质',
      },
    ],
  },
  // 订单
  {
    path: 'oms',
    icon: <ShoppingCart className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.order} />,
    sort: 3,
    children: [
      {
        path: 'checkout-counter',
        label: <Trans i18nKey={LOCALS.checkout_counter} />,
      },
      {
        path: 'order-list',
        label: <Trans i18nKey={LOCALS.order_list} />,
      },
      {
        path: 'order-list-mobile',
        label: <Trans i18nKey={LOCALS.order_list_mobile} />,
      },
      {
        path: 'order-create',
        label: <Trans i18nKey={LOCALS.create_order} />,
      },
      // {
      //   path: 'order-create',
      //   icon: <FileAddOutlined />,
      //   label: <Trans i18nKey={LOCALS.create_order} />,
      // },
    ],
  },
  // 会员
  {
    path: 'ums',
    icon: <Users className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.member} />,
    sort: 4,
    children: [
      {
        path: 'member-list',
        label: <Trans i18nKey={LOCALS.member_list} />,
      },
      {
        path: 'member-add',
        label: <Trans i18nKey={LOCALS.member_sign_up} />,
      },
      {
        path: 'point-history',
        label: '积分记录',
      },
    ],
  },
  {
    path: 'rrs',
    icon: <ShoppingBag className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.menu_rrs} />,
    sort: 5,
    children: [
      {
        // 回收寄卖订单列表
        path: 'recycling-consignment-list',
        label: <Trans i18nKey={LOCALS.sell_your_bag_order} />,
      },

      {
        path: 'recycling-list',
        label: <Trans i18nKey={LOCALS.recycling_list} />,
      },
      {
        path: 'consignment-list',
        label: <Trans i18nKey={LOCALS.consignment_list} />,
      },
      // {
      //   path: 'recycling-contract-order',
      //   label: <Trans i18nKey={LOCALS.recyclingContractOrder} />,
      // },
      // {
      //   path: 'consignment-contract-order',
      //   label: <Trans i18nKey={LOCALS.consignmentContractOrder} />,
      // },
      {
        path: 'recycling-consignment-order',
        label: <Trans i18nKey={LOCALS.contract_order} />,
      },
      // {
      //   // 回收寄卖创建意向订单
      //   path: 'recycling-consignment-intention',
      //   icon: <UnorderedListOutlined />,
      //   label: <Trans i18nKey={LOCALS.intentional_order} />,
      // },
      {
        path: 'reception-list',
        label: <Trans i18nKey={LOCALS.reception_form} />,
      },
      {
        // 回收寄卖预约列表
        path: 'appointment-management-list',
        label: <Trans i18nKey={LOCALS.appointment_management_list} />,
      },
      {
        // 回收寄卖预约配置
        path: 'appointment-management-config',
        label: <Trans i18nKey={LOCALS.appointment_management_config} />,
      },
    ],
  },
  // 营销
  {
    path: 'sms',
    icon: <TrendingUp className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.menu_marketing} />,
    sort: 7,
    children: [
      {
        path: 'ad-list',
        label: <Trans i18nKey={LOCALS.advertisement_list} />,
      },
      {
        path: 'member-mail-template',
        label: <Trans i18nKey={LOCALS.menu_email_template} />,
      },
      {
        path: 'member-mail-send',
        label: <Trans i18nKey={LOCALS.menu_send_email} />,
      },
      {
        path: 'coupon-list',
        label: <Trans i18nKey={LOCALS.menu_discount_configuration} />,
      },
      {
        path: 'coupon-history-list',
        label: <Trans i18nKey={LOCALS.menu_coupon_record} />,
      },
      {
        path: 'integral-list',
        label: <Trans i18nKey={LOCALS.pointsMarketing} />,
      },
    ],
  },
  // 内容
  {
    path: 'cms',
    icon: <FileText className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.menu_cms} />,
    sort: 8,
    children: [
      {
        path: 'article-list',
        label: <Trans i18nKey={LOCALS.article_list} />,
      },
      {
        path: 'article-category',
        label: <Trans i18nKey={LOCALS.article_category} />,
      },
      {
        path: 'subject-list',
        label: <Trans i18nKey={LOCALS.topic_list} />,
      },
      {
        path: 'subject-category',
        label: <Trans i18nKey={LOCALS.topic_category} />,
      },
      {
        path: 'special-contents',
        label: <Trans i18nKey={LOCALS.ZdIlKfghFw} />,
      },
    ],
  },
  {
    path: 'fms',
    icon: <Calculator className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.menu_fms} />,
    sort: 9,
    children: [
      {
        path: 'account-list',
        label: <Trans i18nKey={LOCALS.menu_fms_account} />,
      },
      // {
      //   path: 'shop-cash',
      //   label: <Trans i18nKey={LOCALS.shop_cash} />,
      // },
      // {
      //   path: 'sys-summary',
      //   label: '実績表',
      // },
      // {
      //   path: 'cash-settlement',
      //   label: <Trans i18nKey={LOCALS.rmRaikouFN} />,
      // },
      {
        path: 'daily-report',
        label: <Trans i18nKey={LOCALS.daily_statistics_report} />,
      },
      {
        path: 'profit-report',
        label: <Trans i18nKey={LOCALS.profit_report} />,
      },
      {
        path: 'commission-snapshot',
        label: <Trans i18nKey={LOCALS.commission_snapshot} />,
      },
      {
        path: 'shop-sale-incentive',
        label: <Trans i18nKey={LOCALS.shop_sale_incentive} />,
      },
    ],
  },
  {
    path: 'report',
    icon: <Home className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.menu_home} />,
    sort: 1,
    children: [
      {
        path: 'statement',
        label: <Trans i18nKey={LOCALS.nikkei_table} />,
      },
      {
        path: 'attendance',
        label: <Trans i18nKey={LOCALS.attendance} />,
      },
    ],
  },
  // 系统
  {
    path: 'sys',
    icon: <Settings className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.system} />,
    sort: 10,
    children: [
      {
        path: 'admin-list',
        label: <Trans i18nKey={LOCALS.account_list} />,
      },
      {
        path: 'admin-login-log',
        label: '登录日志',
      },
      {
        path: 'file-upload-log',
        label: '文件上传日志',
      },
      {
        path: 'ip-blacklist',
        label: 'IP黑名单',
      },
      // {
      //   path: 'seoConfig',
      //   icon: <UnorderedListOutlined />,
      //   label: <Trans i18nKey={LOCALS.menu_seo} />,
      // },
      {
        path: 'menu',
        label: <Trans i18nKey={LOCALS.menu_list} />,
      },
      {
        path: 'resource',
        label: <Trans i18nKey={LOCALS.resource_list} />,
      },
      {
        path: 'role',
        label: <Trans i18nKey={LOCALS.role_list} />,
      },
      {
        path: 'dict',
        label: '数据字典',
      },
    ],
  },
  // 该路由代码写死 全部用户拥有
  {
    path: 'sop',
    sort: 100,
    icon: <BarChart3 className="w-5 h-5" />,
    label: <Trans i18nKey={LOCALS.sop} />,
  },
];

/**
 *
 * @description 过滤用户信息中的菜单，只保留用户有权限的菜单
 * @param menus 用户信息中的菜单
 * @returns 过滤后的菜单列表
 */

export const getFilteredMenus = (menus: UserInfoState['menus']) => {
  const filteredMenus: MenuConfigItem[] = [];

  const groupedMenus = groupBy(menus, (menu) => menu.parentId);
  // 获取第一级菜单并排序
  const firstLevelMenus = sortBy(groupedMenus[0] || [], (menu) => {
    return -menu.sort;
  });

  firstLevelMenus.forEach(({ name, id }) => {
    // 查找第一级菜单是否出现在配置里
    const targetMenu = ALL_MENU_CONFIG_ITEMS.find(({ path }) => path === name);
    if (!targetMenu) return;

    const targetMenuCopy = cloneDeep(targetMenu);
    targetMenuCopy.key = `/${targetMenuCopy.path}`;

    // 如果存在 children，还需要过滤 children
    if (targetMenuCopy.children) {
      targetMenuCopy.children = targetMenuCopy.children
        .filter(({ path }) => {
          if (!groupedMenus[id]) return false;

          return !!groupedMenus[id].find(({ name }) => name === path);
        })
        .map((menu) => {
          return {
            ...menu,
            key: `${targetMenuCopy.key}/${menu.path}`,
          };
        });
    }

    // fix: 对于没有二级菜单权限的情况，也不要展示一级菜单
    if (targetMenuCopy.children && targetMenuCopy.children.length === 0) {
      return;
    }

    filteredMenus.push(targetMenuCopy);
  });

  return sortBy(filteredMenus, function (o) {
    return o.sort;
  });
};

// 校验用户路由权限
export const checkRouteRight = (
  isLoading: boolean,
  menus: UserInfoState['menus'],
  pathname: string,
): boolean => {
  if (isLoading) return true;

  if (!menus.length) return false;

  const pathnameArr = pathname.split('/').filter((i) => !!i);
  const [firstLevel, secondLevel] = pathnameArr;

  // 一级菜单
  const targetFirstLevelMenu = menus.find((menu) => {
    return menu.level === 0 && menu.name === firstLevel;
  });

  if (!secondLevel) {
    return !!targetFirstLevelMenu;
  }

  const secondLevelMenus = menus.filter((menu) => {
    return menu.parentId === targetFirstLevelMenu?.id;
  });

  // 二级菜单
  const targetSecondLevelMenu = secondLevelMenus.find((menu) => {
    return menu.name === secondLevel;
  });

  return !!targetSecondLevelMenu;
};

export const getFirstMenuPath = (menus: MenuConfigItem[]) => {
  const [firstMenu] = menus;

  if (!firstMenu) return '';

  if (firstMenu.children && firstMenu.children.length) {
    return firstMenu.children[0].key;
  } else {
    return firstMenu.key;
  }
};

// 根据用户信息的 menus 和 ALL_MENU_CONFIG_ITEMS 配置，获取实际最终用来渲染左侧菜单列表的数据
export const useMenuItems = () => {
  const userInfo = useSelector(selectUserInfo);
  const [menuItems, setMenuItems] = useState<MenuConfigItem[]>([]);

  useEffect(() => {
    setMenuItems(getFilteredMenus(userInfo.menus));
  }, [userInfo.menus]);

  return menuItems;
};
